import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "JS object optimization bug in Chrome 52",
  "description": "",
  "published": "2016-08-10T00:00:00.000Z",
  "redirect_from": ["/blog/js-object-optimization-bug-in-chrome-52/swizec/6908"],
  "categories": "Front End, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Dear diary, all I did today was fix 1 bug. I don't know why it happens, but I know how to make it not happen.`}<a parentName="p" {...{
          "href": "https://twitter.com/hashtag/engineering?src=hash&ref_src=twsrc%5Etfw"
        }}>{`#engineering`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/761722056681545729"
      }}>{`August 6, 2016`}</a></blockquote>
    <p>{`That was on Friday. I still don’t know `}<em parentName="p">{`why`}</em>{` the bug happens, but I can reproduce it. Evidence says it’s a Chrome 52 bug, not a general JavaScript bug. Neither Safari nor Firefox make it happen.`}</p>
    <p>{`Sometimes when you call `}<inlineCode parentName="p">{`.fetch()`}</inlineCode>{` on a Backbone model, callbacks don’t fire. There’s no JavaScript error, the REST call works, the result is vanished by the browser. Joy of joys.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/08/chrome-eats-objects.gif",
        "alt": "2 fetches, 1 callback"
      }}></img></p>
    <p>{`You need:`}</p>
    <ul>
      <li parentName="ul">{`Webpack`}</li>
      <li parentName="ul">{`Babel 6`}</li>
      <li parentName="ul">{`Backbone (maybe Backbone is doing it wrong?)`}</li>
      <li parentName="ul">{`A server that can serve JSON files`}</li>
      <li parentName="ul">{`14 lines of code`}</li>
    </ul>
    <p>{`I used `}<inlineCode parentName="p">{`create-react-app`}</inlineCode>{` to set up a basic environment, ejected with `}<inlineCode parentName="p">{`npm run eject`}</inlineCode>{` and added some config to Webpack to allow jQuery. Backbone depends on it. This part is not `}<em parentName="p">{`that`}</em>{` relevant.`}</p>
    <p>{`To show the bug itself, we need a Backbone model:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// A basic Backbone model
class BugModel extends Backbone.Model {
  constructor() {
    super();

    this.url = "bla.json";
  }
}
`}</code></pre>
    <p>{`Now I know what you’re thinking, that’s an ES6 object and Backbone was built before ES6. Official docs never mention the new `}<inlineCode parentName="p">{`class`}</inlineCode>{` syntax. The traditional Backbone approach works too:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var BugModel = Backbone.Model.extend({
  url: "bla.json",
});
`}</code></pre>
    <p>{`The same bug happens. I tried.`}</p>
    <p>{`To cause the bug, we have to call `}<inlineCode parentName="p">{`.fetch()`}</inlineCode>{` on two instances of `}<inlineCode parentName="p">{`BugModel`}</inlineCode>{` that share the same `}<inlineCode parentName="p">{`id`}</inlineCode>{`. The code looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`let bug = new BugModel();
bug.fetch({
  success: () => {
    console.log("fetch 1"); // this happens
    doWeirdness(bug);
  },
});

function doWeirdness(bug) {
  let newBug = new BugModel({ id: bug.id });

  console.log("about to re-fetch");

  newBug.fetch({
    success: () => console.log("fetch 2", newBug), // don't get here
    error: () => console.log("error"),
  });
}
`}</code></pre>
    <p>{`We create an instance of `}<inlineCode parentName="p">{`BugModel`}</inlineCode>{` in a variable called `}<inlineCode parentName="p">{`bug`}</inlineCode>{`, then call `}<inlineCode parentName="p">{`.fetch()`}</inlineCode>{`. This makes an API call to `}<inlineCode parentName="p">{`/bla.json`}</inlineCode>{`, which returns a JSON file that looks like this: `}<inlineCode parentName="p">{`{"id": 1, "hai": "hello"}`}</inlineCode>{`.`}</p>
    <p>{`Backbone automagically parses this JSON string and sets an `}<inlineCode parentName="p">{`id`}</inlineCode>{` and `}<inlineCode parentName="p">{`hai`}</inlineCode>{` attribute. This part works like a charm.`}</p>
    <p>{`Then we call `}<inlineCode parentName="p">{`doWeirdness`}</inlineCode>{` and pass our `}<inlineCode parentName="p">{`bug`}</inlineCode>{` object as an attribute. Inside `}<inlineCode parentName="p">{`doWeirdness`}</inlineCode>{`, we make a new instance of `}<inlineCode parentName="p">{`BugModel`}</inlineCode>{` called `}<inlineCode parentName="p">{`newBug`}</inlineCode>{` and give it the same `}<inlineCode parentName="p">{`id`}</inlineCode>{` as `}<inlineCode parentName="p">{`bug`}</inlineCode>{` had. This is a crucial step.`}</p>
    <p>{`When we call `}<inlineCode parentName="p">{`fetch`}</inlineCode>{` on this new instance, the API call happens, but the callbacks do not. `}<inlineCode parentName="p">{`'fetch 2'`}</inlineCode>{` never prints.`}</p>
    <p>{`This whole sequence of events might sound redundant, but it made sense in my real codebase. Even if re-fetching objects doesn’t make sense, it `}<em parentName="p">{`should`}</em>{` work. It works in both Safari and Firefox.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/08/safari-does-not.gif",
        "alt": "Works in Safari"
      }}></img></p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/08/firefox-works-too.gif",
        "alt": "Works in Firefox"
      }}></img></p>
    <h2 {...{
      "id": "clues-about-why"
    }}>{`Clues about why`}</h2>
    <p>{`This smells like a bug in memory optimization. Chrome thinks `}<inlineCode parentName="p">{`bug`}</inlineCode>{` and `}<inlineCode parentName="p">{`newBug`}</inlineCode>{` are the same object, even though they are not.`}</p>
    <p>{`If you call `}<inlineCode parentName="p">{`fetch()`}</inlineCode>{` twice on the same instance of `}<inlineCode parentName="p">{`BugModel`}</inlineCode>{`, the same bug happens. (clue, part 1)`}</p>
    <p>{`If both instances have a different `}<inlineCode parentName="p">{`id`}</inlineCode>{`, both `}<inlineCode parentName="p">{`fetch`}</inlineCode>{` calls fire callbacks. (clue, part 2)`}</p>
    <p>{`Curiously, if you add a third `}<inlineCode parentName="p">{`fetch()`}</inlineCode>{` call, that works.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`newBug.fetch({
  success: () => console.log("fetch 2", newBug), // don't get here
  error: () => console.log("error"),
});

newBug.fetch({
  success: () => console.log("fetch 3"), // prints
});
`}</code></pre>
    <p>{`The bug didn’t happen in Chrome 51.`}</p>
    <p>{`I am confused. ?`}</p>
    <p>{`PS: `}<a parentName="p" {...{
        "href": "http://swizec.com/blog/i-broke-ajax-in-chrome-52-%f0%9f%98%b1/swizec/6914"
      }}>{`there is a followup`}</a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      